/** P20–P23 — civilizations, stats, costs. */

import { Kind, type Civ, type TeamEco } from './engine';
import { STARHOLD_PALETTE as P } from './palette';

export const CIV_NAME: Record<Civ, string> = {
  vespari: 'Sunweaver',
  aurion: 'Gravemark',
  voidmarked: 'Nihiline',
};

export const ALL_CIVS: Civ[] = ['vespari', 'aurion', 'voidmarked'];

export interface CivProfile {
  subtitle: string;
  doctrine: string;
  edge: string;
  plan: string;
  startOre: number;
  startGas: number;
  startEnergy: number;
}

/** Opening identities are intentionally different before technology paths diverge. */
export const CIV_PROFILE: Record<Civ, CivProfile> = {
  vespari: {
    subtitle: 'Sunward network',
    doctrine: 'Solar geometry',
    edge: 'Flexible forward control',
    plan: 'Scout wide, then compress the frontier with energy and speed.',
    startOre: 220,
    startGas: 40,
    startEnergy: 110,
  },
  aurion: {
    subtitle: 'Ice cathedral',
    doctrine: 'Cold precision',
    edge: 'Durable positions',
    plan: 'Freeze the approach, hold long sight lines, and punish overreach.',
    startOre: 250,
    startGas: 45,
    startEnergy: 70,
  },
  voidmarked: {
    subtitle: 'Void mycelium',
    doctrine: 'Spore pressure',
    edge: 'Stealth and disruption',
    plan: 'Make unsafe ground, strike from concealment, and break the rhythm.',
    startOre: 200,
    startGas: 70,
    startEnergy: 90,
  },
};

/** Opponent for skirmish — always a different people. */
export function enemyCiv(player: Civ): Civ {
  if (player === 'vespari') return 'aurion';
  if (player === 'aurion') return 'vespari';
  return 'aurion';
}

export function parseBootCiv(search: string): Civ | null {
  const q = new URLSearchParams(search);
  const raw = q.get('civ') ?? q.get('p');
  if (!raw) return null;
  return ALL_CIVS.includes(raw as Civ) ? (raw as Civ) : null;
}

function rgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
}

/** Team accents are fixed to the Starhold tokens, not ad-hoc neon colors. */
export const TEAM_RGB: [number, number, number][] = [
  rgb(P.leaf),
  rgb(P.red),
  rgb(P.sky),
  rgb(P.amber),
];

export interface Stats {
  hp: number;
  spd: number;
  atk: number;
  range: number;
  los: number;
  radius: number;
  ore: number;
  gas: number;
  energy: number;
  train: number;
  pop: number;
  melee: boolean;
  building: boolean;
}

export const STATS: Record<number, Stats> = {
  [Kind.Worker]: {
    hp: 45, spd: 1.85, atk: 5, range: 0.85, los: 4.5, radius: 0.28,
    ore: 50, gas: 0, energy: 0, train: 7, pop: 1, melee: true, building: false,
  },
  [Kind.Scout]: {
    hp: 40, spd: 3.15, atk: 5, range: 0.8, los: 9.5, radius: 0.26,
    ore: 40, gas: 0, energy: 15, train: 6, pop: 1, melee: true, building: false,
  },
  [Kind.Fighter]: {
    hp: 96, spd: 1.85, atk: 10, range: 3.1, los: 6, radius: 0.32,
    ore: 70, gas: 0, energy: 20, train: 10, pop: 2, melee: false, building: false,
  },
  [Kind.Siege]: {
    hp: 55, spd: 1.15, atk: 30, range: 6.2, los: 5, radius: 0.42,
    ore: 90, gas: 40, energy: 20, train: 16, pop: 2, melee: false, building: false,
  },
  [Kind.Ravager]: {
    hp: 96, spd: 2.35, atk: 15, range: 0.95, los: 5.5, radius: 0.36,
    ore: 90, gas: 35, energy: 20, train: 16, pop: 2, melee: true, building: false,
  },
  [Kind.Prism]: {
    hp: 165, spd: 0.95, atk: 24, range: 6.2, los: 7, radius: 0.48,
    ore: 110, gas: 45, energy: 30, train: 20, pop: 3, melee: false, building: false,
  },
  [Kind.Shade]: {
    hp: 48, spd: 2.75, atk: 8, range: 3.4, los: 10.5, radius: 0.28,
    ore: 65, gas: 40, energy: 15, train: 11, pop: 1, melee: false, building: false,
  },
  [Kind.Hall]: {
    hp: 1400, spd: 0, atk: 0, range: 0, los: 8, radius: 1.35,
    ore: 350, gas: 0, energy: 0, train: 0, pop: 0, melee: true, building: true,
  },
  [Kind.House]: {
    hp: 420, spd: 0, atk: 0, range: 0, los: 4, radius: 0.85,
    ore: 60, gas: 0, energy: 0, train: 0, pop: 0, melee: true, building: true,
  },
  [Kind.Barracks]: {
    hp: 780, spd: 0, atk: 0, range: 0, los: 5, radius: 1.05,
    ore: 150, gas: 0, energy: 20, train: 0, pop: 0, melee: true, building: true,
  },
  [Kind.UniqueB]: {
    hp: 560, spd: 0, atk: 12, range: 6.5, los: 7, radius: 0.95,
    ore: 120, gas: 50, energy: 20, train: 0, pop: 0, melee: false, building: true,
  },
  [Kind.Resource]: {
    hp: 220, spd: 0, atk: 0, range: 0, los: 0, radius: 0.7,
    ore: 0, gas: 0, energy: 0, train: 0, pop: 0, melee: true, building: true,
  },
};

export function hallName(_civ: Civ): string {
  return 'Nexus';
}

export function houseName(_civ: Civ): string {
  return 'Habitat';
}

export function barracksName(_civ: Civ): string {
  return 'Yard';
}

export function uniqueName(civ: Civ): string {
  if (civ === 'vespari') return 'Sunwell';
  if (civ === 'aurion') return 'Cryo Bastion';
  return 'Bloom Nest';
}

export function uniqueUnit(civ: Civ): Kind {
  if (civ === 'vespari') return Kind.Ravager;
  if (civ === 'aurion') return Kind.Prism;
  return Kind.Shade;
}

export function workerName(_civ: Civ): string {
  return 'Worker';
}

export function fighterName(civ: Civ): string {
  return civ === 'vespari' ? 'Lumen Guard' : 'Rift Guard';
}

export function labelOf(kind: Kind, civ: Civ): string {
  if (kind === Kind.Hall) return hallName(civ);
  if (kind === Kind.House) return houseName(civ);
  if (kind === Kind.Barracks) return barracksName(civ);
  if (kind === Kind.UniqueB) return uniqueName(civ);
  if (kind === Kind.Worker) return workerName(civ);
  if (kind === Kind.Fighter) return fighterName(civ);
  if (kind === Kind.Ravager) return 'Solar Strider';
  if (kind === Kind.Prism) return 'Burden Walker';
  if (kind === Kind.Shade) return 'Spore Rider';
  if (kind === Kind.Scout) return civ === 'vespari' ? 'Wind Strider' : 'Grav-Skimmer';
  if (kind === Kind.Siege) return 'Breaker';
  return 'Unknown';
}

export function isBuilding(k: Kind): boolean {
  return k >= Kind.Hall && k < Kind.Resource;
}

export function isUnit(k: Kind): boolean {
  return k < Kind.Hall;
}

export const POP_HOUSE = 5;
export const POP_HALL = 10;
export const GATHER_MAX = 8;
export const BUILD_HP_START = 0.08;

// ---- M4 — technology paths (docs/M4_TECH_PATHS.md) ---------------------------------

export type TechPathId =
  | 'solar-ascendancy'
  | 'sky-dominion'
  | 'iron-colossus'
  | 'rift-engineering';

export interface TechPathInfo {
  id: TechPathId;
  civ: Civ;
  name: string;
  blurb: string;
}

/** One irreversible choice between two paths per faction (CANONICAL_VOCABULARY table). */
export const TECH_PATHS: readonly TechPathInfo[] = [
  {
    id: 'solar-ascendancy',
    civ: 'vespari',
    name: 'Solar Ascendancy',
    blurb: 'Tethers regrow 2x faster · Boost costs reduced 25%',
  },
  {
    id: 'sky-dominion',
    civ: 'vespari',
    name: 'Sky Dominion',
    blurb: 'Ships move 12% faster · Wind Striders see 2 farther',
  },
  {
    id: 'iron-colossus',
    civ: 'aurion',
    name: 'Iron Colossus',
    blurb: 'Rigs absorb 50% more damage · extract 33% faster',
  },
  {
    id: 'rift-engineering',
    civ: 'aurion',
    name: 'Rift Engineering',
    blurb: 'Ranged reach +1 · Burden Walkers train 30% faster',
  },
];

export function pathsForCiv(civ: Civ): TechPathId[] {
  return TECH_PATHS.filter((p) => p.civ === civ).map((p) => p.id);
}

/** M4 decision 4 — every path number lives in this one table; tests assert plumbing. */
export interface PathEffects {
  /** solar-ascendancy — link re-form delay scale after a sever. */
  linkSeverScale: number;
  /** solar-ascendancy — boost energy drain multiplier. */
  boostDrainMul: number;
  /** sky-dominion — non-worker unit move speed multiplier. */
  combatSpeedMul: number;
  /** sky-dominion — flat Scout LOS bonus. */
  scoutLosBonus: number;
  /** iron-colossus — finished rig HP multiplier. */
  rigHpMul: number;
  /** iron-colossus — rig extraction interval, seconds per unit. */
  rigExtractSec: number;
  /** rift-engineering — flat non-melee attack range bonus. */
  rangedRangeBonus: number;
  /** rift-engineering — Prism-kind train time multiplier. */
  siegeTrainMul: number;
}

const NEUTRAL_EFFECTS: PathEffects = {
  linkSeverScale: 1,
  boostDrainMul: 1,
  combatSpeedMul: 1,
  scoutLosBonus: 0,
  rigHpMul: 1,
  rigExtractSec: 1,
  rangedRangeBonus: 0,
  siegeTrainMul: 1,
};

export const PATH_EFFECTS: Record<TechPathId, PathEffects> = {
  'solar-ascendancy': { ...NEUTRAL_EFFECTS, linkSeverScale: 0.5, boostDrainMul: 0.75 },
  'sky-dominion': { ...NEUTRAL_EFFECTS, combatSpeedMul: 1.12, scoutLosBonus: 2 },
  'iron-colossus': { ...NEUTRAL_EFFECTS, rigHpMul: 1.5, rigExtractSec: 0.75 },
  'rift-engineering': { ...NEUTRAL_EFFECTS, rangedRangeBonus: 1, siegeTrainMul: 0.7 },
};

/** An uncommitted team plays with the identity numbers. */
export function pathEffects(path: TechPathId | null): PathEffects {
  return path ? PATH_EFFECTS[path] : NEUTRAL_EFFECTS;
}

const GATED_KINDS: readonly Kind[] = [
  Kind.Fighter,
  Kind.Siege,
  Kind.Ravager,
  Kind.Prism,
  Kind.Shade,
];

/** Kinds locked behind a committed technology path (M4 decision 1). */
export function isPathGated(kind: Kind): boolean {
  return GATED_KINDS.includes(kind);
}

/** Gate helper — gated kinds train only once the team has committed. */
export function gateOpen(eco: Pick<TeamEco, 'techPath'>, kind: Kind): boolean {
  return !isPathGated(kind) || eco.techPath !== null;
}
